import { useMountedState } from '@flippingbook/web.react-hooks/dist/useMountedState';
import type { ServiceError } from '@flippingbook/web.utils/dist/infrastructure-interfaces/ServiceError';
import type {
    ErrorousServiceResponse,
    ServiceResponse,
} from '@flippingbook/web.utils/dist/infrastructure-interfaces/ServiceResponse';
import { ValidationMessage } from '@flippingbook/web.utils/dist/infrastructure-interfaces/ValidationMessage';
import { parseValidationErrors } from '@flippingbook/web.utils/dist/util/parseValidationErrors';
import { InternalServerError } from '@flippingbook/web.utils/dist/webservice/ServiceClient';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { loc } from '@/localization';
import { TokenType } from '@/services/apis/interfaces/TokenType';
import { optionsService } from '@/services/instance/optionsService';
import { sso } from '@/services/instance/sso';
import { getLoginUrl } from '@/utils/getPageUrl';
import { registerPassword } from '@/utils/validationRules';

const validationRules = {
    password: registerPassword,
} as const;

interface FormDataType {
    password: string;
}

const serverPasswordErrorMap: Record<ValidationMessage, string> = {
    [ValidationMessage.ValueTooShort]: loc.Validation.PasswordValueTooShort,
    [ValidationMessage.ValueMandatory]: loc.Validation.PasswordMandatory,
    [ValidationMessage.InvalidValue]: loc.Validation.InvalidPassword,
    [ValidationMessage.DuplicateValue]: loc.Validation.PasswordDoNotMatch,
    [ValidationMessage.ValueTooLong]: loc.Validation.PasswordValueTooLong,
    [ValidationMessage.WrongData]: loc.Validation.InvalidPassword,
};

export function useRestoreUpdatePage() {
    const isMounted = useMountedState();

    const [searchParams] = useSearchParams();

    const email = searchParams.get('email') || undefined;
    const ticket = searchParams.get('ticket') || undefined;

    const {
        register,
        handleSubmit,
        trigger: triggerValidation,
        setError,
        formState: { errors, isSubmitting, isSubmitted },
        watch,
    } = useForm<FormDataType>({
        shouldFocusError: false,
        criteriaMode: 'all',
        defaultValues: {
            password: '',
        },
    });

    const loginUrl = getLoginUrl({
        ...optionsService.getQueryArgs(),
        email,
    });

    const sendRequest = useCallback(
        (password: string) => {
            const tokenType = optionsService.getTokenType() === 'jwt' ? TokenType.JWT : TokenType.AccessToken;
            const returnToken = TokenType.Persistent | tokenType;

            return sso.resetPassword({
                password,
                returnToken,
                ticket: ticket || '',
            });
        },
        [ticket],
    );

    const submitForm = useCallback(
        async (data: FormDataType) => {
            const formValid = await triggerValidation();

            if (!formValid) {
                return;
            }

            try {
                const response = await sendRequest(data.password);

                if (isMounted()) {
                    await sso.return(response);
                }
            } catch (error) {
                if (!isMounted()) {
                    return;
                }

                const isPasswordPolicyErrorCode = (error: ServiceError) => error.ErrorCode === 'PasswordPolicy';

                const hasPasswordPolicyError = (error: unknown) => {
                    if (error instanceof InternalServerError) {
                        const responseData = error.getData().response.responseData as ServiceResponse;

                        if (responseData instanceof Object && !responseData.Success) {
                            return responseData.Errors.some(isPasswordPolicyErrorCode);
                        }

                        return false;
                    }

                    const erroneousServiceResponse = (error as { data: ErrorousServiceResponse }).data;

                    return erroneousServiceResponse.Errors.some(isPasswordPolicyErrorCode);
                };

                const erroneousServiceResponse = (error as { data: ErrorousServiceResponse }).data;

                if (hasPasswordPolicyError(error)) {
                    setError('password', {
                        type: 'server',
                        message: loc.Validation.PasswordPolicyErrorMessage,
                    });
                    return;
                }

                const validationErrors = parseValidationErrors(erroneousServiceResponse);
                const passwordErrors = validationErrors.password;

                if (passwordErrors && passwordErrors.length) {
                    const [firstError] = passwordErrors;
                    const errorLocalized = serverPasswordErrorMap[firstError] || loc.Validation.PasswordInvalid;

                    setError('password', {
                        type: 'server',
                        message: errorLocalized,
                    });
                    return;
                }

                throw error;
            }
        },
        [triggerValidation, isMounted, sendRequest, setError],
    );

    const onFormSubmit = useCallback(
        (ev: React.BaseSyntheticEvent) => {
            void handleSubmit(submitForm)(ev);
        },
        [handleSubmit, submitForm],
    );

    return {
        loginUrl,
        errors,
        isSubmitting,
        isSubmitted,
        validationRules,
        register,
        triggerValidation,
        onFormSubmit,
        watch,
    };
}
